import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { ButtonLeadingIcon, PrimaryButton, SecondaryLinkButton } from '../components/Button'
import { Link } from 'react-router-dom'
import Table from '../components/Table'
import { errorToast, successToast, formatCurrency } from '../util/Util'
import Radom, { IDiscountCode } from '../state/Radom'
import Dots from '../icons/Dots'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import CatalogTabs from '../components/CatalogTabs'
import PageHeader from '../components/PageHeader'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import TimeText from '../components/TimeText'
import { Container } from '../components/Animations'
import { StatusBadge } from './CheckoutSessions'
import { isBefore } from 'date-fns'
import {
  EmptyState,
  ListContent,
  ListPage,
  PageActionRow,
  PageHeaderRow
} from '../components/DashboardPrimitives'
import Gate from '../components/Gate'

const DiscountCodesView = observer(() => {
  const [discountCodes, setDiscountCodes] = useState([] as IDiscountCode[])
  const [isLoadingDiscountCodes, setIsLoadingDiscountCodes] = useState(false)

  const loadDiscountCodes = (): void => {
    setIsLoadingDiscountCodes(true)
    Radom.listDiscountCodes().then((codes) => {
      setDiscountCodes(codes)
    }).finally(() => setIsLoadingDiscountCodes(false))
  }

  useEffect(() => {
    loadDiscountCodes()
  }, [])

  return <Container>
    <ListPage>
      <PageHeaderRow>
        <PageHeader title='Discount codes' subTitle='Create and manage discount codes' />
        <Gate permission={['WriteDiscountCodes', 'ReadProducts']}>
          <PageActionRow>
            <Link key="createDiscountCode" to="/discounts/create" style={{ textDecoration: 'none' }}>
              <PrimaryButton>
                <ButtonLeadingIcon>+</ButtonLeadingIcon>
                <span>Create discount code</span>
              </PrimaryButton>
            </Link>
          </PageActionRow>
        </Gate>
      </PageHeaderRow>

      <CatalogTabs active='discounts' />

      <ListContent>
        {
          !isLoadingDiscountCodes && discountCodes.length === 0
            ? <EmptyState style={{ flexDirection: 'column', gap: 12 }}>
              <span>No discount codes yet. Codes apply a fixed or percentage discount at checkout.</span>
              <Gate permission={['WriteDiscountCodes', 'ReadProducts']}>
                <SecondaryLinkButton
                  to="/discounts/create"
                  style={{ minHeight: 36, fontSize: 13, padding: '0 14px', gap: 7 }}>
                  <ButtonLeadingIcon>+</ButtonLeadingIcon>
                  <span>Create discount code</span>
                </SecondaryLinkButton>
              </Gate>
            </EmptyState>
            : <Table
              borderless
              headers={['Code', 'Status', 'Discount', 'Products', 'Starts at', 'Expires at', ''].map(h => <span key={h}>{h}</span>)}
              tableContainerStyle={{
                gridTemplateColumns:
                  'minmax(140px, 1.2fr) minmax(100px, 0.8fr) minmax(120px, 0.9fr) ' +
                  'minmax(90px, 0.6fr) minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(52px, auto)'
              }}
              items={[...discountCodes]
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                .map((d, i) => {
                  let status = <StatusBadge status='active'>Active</StatusBadge>
                  if (d.startsAt && isBefore(new Date(), new Date(d.startsAt))) {
                    status = <StatusBadge status='pending'>Pending</StatusBadge>
                  }
                  if (d.expiresAt && isBefore(new Date(d.expiresAt), new Date())) {
                    status = <StatusBadge status='expired'>Expired</StatusBadge>
                  }

                  return [
                    <div key="code">
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 6,
                        backgroundColor: 'var(--surface-pressed, #eef1f7)',
                        color: 'var(--ink, #20242d)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12.5
                      }}>{d.code}</span>
                    </div>,
                    <div key="status">
                      {status}
                    </div>,
                    <div key="discount">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{
                          d.model.modelType === 'percentage'
                            ? new Intl.NumberFormat('en-US', {
                              style: 'percent',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(d.model.amountOff / 100)
                            : formatCurrency(d.model.amountOff, 'USD')
                        }</span>
                        <span style={{
                          fontSize: 12,
                          color: 'var(--text-faint, #8a919e)'
                        }}>
                          {d.model.modelType === 'percentage' ? 'Percentage' : 'Fixed'}
                        </span>
                      </div>
                    </div>,
                    <div key="products" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.products.length}</div>,
                    <div key="starts" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {
                        d.startsAt
                          ? <TimeText date={new Date(d.startsAt)} />
                          : <span style={{ color: 'var(--text-faint, #8a919e)' }}>-</span>
                      }
                    </div>,
                    <div key="expires" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {
                        d.expiresAt
                          ? <TimeText date={new Date(d.expiresAt)} />
                          : <span style={{ color: 'var(--text-faint, #8a919e)' }}>-</span>
                      }
                    </div>,
                    <div key={`${i}${d.code}`} style={{ display: 'flex' }}>
                      <Gate permission='WriteDiscountCodes'>
                        <Dropdown
                          noChevron
                          iconTrigger
                          selectedContent={<Dots width={15} />}
                          dropdownContent={
                            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                              <ConfirmButtonModal
                                title='Delete discount code'
                                description='Are you sure you want to delete this discount code? This action cannot be undone.'
                                action='Delete discount code'
                                onConfirm={async () => {
                                  await Radom.deleteDiscountCode(d.id)
                                    .then(() => {
                                      successToast('Successfully deleted discount code')
                                      loadDiscountCodes()
                                    })
                                    .catch(err => {
                                      errorToast(err.response?.data?.error || err.message)
                                    })
                                }}
                                ActionComponent={props => <DropdownItem {...props}>Delete discount code</DropdownItem>}
                              />
                            </div>
                          }
                        />
                      </Gate>
                    </div>
                  ]
                })}
              isLoading={isLoadingDiscountCodes}
              emptyPlaceholderText="No discount codes yet"
            />
        }
      </ListContent>
    </ListPage>
  </Container>
})

export default DiscountCodesView
